import React from 'react'
import PropTypes from 'prop-types'
import {useTranslation} from 'react-i18next'
import useSWR from 'swr'
import Dinero from 'dinero.js'
import {
  Box,
  Divider,
  Flex,
  Link,
  Skeleton,
  Stack,
  StackDivider,
  Text,
} from '@chakra-ui/react'
import {ChevronRightIcon} from '@chakra-ui/icons'
import {fetcher} from '../../../../../../api'
import {
  camelizeResult,
  dineroMoneyObjects,
} from '../../../../../../api/serializer'
import {useRails} from '../../../../../contexts/rails'
import DineroPropShape from '../../../../../../utils/propTypes/dinero'

const splitAmount = (amount) => {
  const [dollars, cents] = (amount || new Dinero({}))
    .toFormat('$0,0.00')
    .split('.')

  return {dollars, cents: `.${cents}`}
}

const AmountDisplay = ({amount, dollarStyle, dataTestId}) => {
  const {dollars, cents} = splitAmount(amount)

  return (
    <Flex align="flex-start" className="fs-mask" data-testid={dataTestId}>
      <Text textStyle={dollarStyle}>{dollars}</Text>
      <Text textStyle="headline5" textTransform="uppercase" pt="xxs">
        {cents}
      </Text>
    </Flex>
  )
}

AmountDisplay.defaultProps = {amount: null}

AmountDisplay.propTypes = {
  amount: PropTypes.shape(DineroPropShape),
  dollarStyle: PropTypes.string.isRequired,
  dataTestId: PropTypes.string.isRequired,
}

const CategoryBreakdown = ({
  label,
  href,
  amount,
  count,
  isLoading,
  dataTestId,
}) => {
  const {t} = useTranslation('supplierDashboard')

  return (
    <Flex direction="column" gap="xs" flex="1" px="xs" py="sm" minWidth="140px">
      <Link
        href={href}
        display="inline-flex"
        alignItems="center"
        gap="xxs"
        width="fit-content"
        color="inherit"
        aria-label={t('activityCard.viewCategory', {category: label})}
        _hover={{textDecoration: 'none', color: 'mld.primary.200'}}
      >
        <Text textStyle="headline5" textTransform="uppercase">
          {label}
        </Text>
        <ChevronRightIcon boxSize="16px" />
      </Link>
      <Skeleton isLoaded={!isLoading} width="fit-content">
        <AmountDisplay
          amount={amount}
          dollarStyle="headline3"
          dataTestId={dataTestId}
        />
      </Skeleton>
      <Text textStyle="body-small" color="mld.neutral.800">
        {t('activityCard.invoices', {count: count || 0})}
      </Text>
    </Flex>
  )
}

CategoryBreakdown.defaultProps = {amount: null, count: null}

CategoryBreakdown.propTypes = {
  label: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
  dataTestId: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  amount: PropTypes.shape(DineroPropShape),
  count: PropTypes.number,
}

const UnpaidInvoicesCard = () => {
  const {t} = useTranslation('supplierDashboard')
  const {organisation} = useRails()
  const {isLoading, data} = useSWR(
    `/api/suppliers/${organisation.currentSupplier.id}/unpaid_invoices_summary`,
    fetcher,
    {
      use: [camelizeResult, dineroMoneyObjects],
    },
  )

  const categories = [
    {
      tab: 'eligible',
      label: t('activityCard.availableNow'),
      amount: data?.eligibleAmount,
      count: data?.eligibleCount,
      dataTestId: 'eligible-amount',
    },
    {
      tab: 'conditionallyEligible',
      label: t('activityCard.conditionallyAvailable'),
      amount: data?.conditionallyEligibleAmount,
      count: data?.conditionallyEligibleCount,
      dataTestId: 'conditionally-available-amount',
    },
    {
      tab: 'ineligible',
      label: t('activityCard.notAvailable'),
      amount: data?.ineligibleAmount,
      count: data?.ineligibleCount,
      dataTestId: 'ineligible-amount',
    },
    {
      tab: 'cashedIn',
      label: t('activityCard.cashedIn'),
      amount: data?.cashedInAmount,
      count: data?.cashedInCount,
      dataTestId: 'cashed-in-amount',
    },
  ]

  return (
    <Box
      className="fs-unmask"
      display="flex"
      flexDirection="column"
      gap="md"
      bg="mld.neutral.0"
      color="mld.neutral.900"
      p="md"
      borderRadius="lg"
      data-testid="unpaid-invoices-card"
    >
      <Text textStyle="headline5" textTransform="uppercase">
        {t('activityCard.title')}
      </Text>

      <Flex justify="space-between" align="flex-start" gap="sm" wrap="wrap">
        <Text textStyle="headline3">{t('activityCard.totalUnpaid')}</Text>
        <Flex direction="column" gap="xs" align="flex-start">
          <Skeleton isLoaded={!isLoading} width="fit-content">
            <AmountDisplay
              amount={data?.totalAmount}
              dollarStyle="headline2"
              dataTestId="total-unpaid-amount"
            />
          </Skeleton>
          <Text textStyle="body-small" color="mld.neutral.800">
            {t('activityCard.invoices', {count: data?.totalCount || 0})}
          </Text>
        </Flex>
      </Flex>

      <Box>
        <Divider borderColor="mld.neutral.500" />
        <Stack
          direction={{base: 'column', md: 'row'}}
          spacing="0"
          divider={<StackDivider borderColor="mld.neutral.500" />}
        >
          {categories.map(({tab, label, amount, count, dataTestId}) => (
            <CategoryBreakdown
              key={tab}
              label={label}
              href={`/cashin?tab=${tab}`}
              amount={amount}
              count={count}
              isLoading={isLoading}
              dataTestId={dataTestId}
            />
          ))}
        </Stack>
      </Box>
    </Box>
  )
}

export default UnpaidInvoicesCard
